import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import type { Language } from '@/types';
import {
  createQCReportsBatch,
  fetchLatestVisit,
  fetchVisitBlocks,
  type QCVisitBlock,
  type QCVisitOption,
} from '../api';

interface QCReportFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteName: string;
  token: string | null;
  language: Language;
  onCreated: () => void;
}

type Score = 1 | 2 | 3 | 4 | 5;

type BlockEntry = {
  block: QCVisitBlock;
  score: Score | null;
  notes: string;
};

const OK_INK = '#1B7F4D';
const FAIL_INK = '#C24138';

const copy = {
  en: {
    title: 'GENOMFÖR QC',
    description: 'Score each block. Notes required when score is 1.',
    loading: 'Loading visit…',
    noVisit: 'No visits to inspect yet.',
    visitSummary: 'INSPECTING VISIT',
    cleaner: 'Cleaner',
    blocksHeader: 'Blocks',
    extras: 'extra',
    recurring: 'recurring',
    photos: 'photos',
    points: 'p',
    notesPlaceholder: 'Notes (required when 1 star)',
    notesRequired: 'Notes are required when score is 1.',
    extrasCheck: 'Inspected extra work',
    average: 'AVG',
    cancel: 'CANCEL',
    submit: 'SKICKA IN',
    submitting: 'Submitting…',
    rated: 'RATED',
    submitErrorPrefix: 'Failed:',
    none: 'No blocks on this visit.',
  },
  sv: {
    title: 'GENOMFÖR QC',
    description: 'Betygsätt varje moment. Anteckning krävs vid 1 stjärna.',
    loading: 'Laddar besök…',
    noVisit: 'Inga besök att inspektera ännu.',
    visitSummary: 'INSPEKTERAR BESÖK',
    cleaner: 'Städare',
    blocksHeader: 'Moment',
    extras: 'extra',
    recurring: 'ordinarie',
    photos: 'bilder',
    points: 'p',
    notesPlaceholder: 'Anteckning (krävs vid 1 stjärna)',
    notesRequired: 'Anteckning krävs vid 1 stjärna.',
    extrasCheck: 'Inspekterade extrastäd',
    average: 'SNITT',
    cancel: 'AVBRYT',
    submit: 'SKICKA IN',
    submitting: 'Skickar…',
    rated: 'BETYGSATTA',
    submitErrorPrefix: 'Misslyckades:',
    none: 'Inga moment för detta besök.',
  },
} as const;

function statusInk(status: string): string {
  if (status === 'completed' || status === 'approved') return OK_INK;
  if (status === 'cancelled') return FAIL_INK;
  if (status === 'skipped') return '#B97E1F';
  return '#607C6E';
}

function StarPicker({
  value,
  onChange,
  disabled,
}: {
  value: Score | null;
  onChange: (next: Score) => void;
  disabled?: boolean;
}) {
  // Filled stars take status ink: 1–2 reads fail, 3+ reads pass.
  const ink = value != null && value < 3 ? FAIL_INK : OK_INK;
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value != null && n <= value;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n as Score)}
            aria-label={`${n} / 5`}
            className="p-0.5 transition-colors disabled:cursor-not-allowed"
          >
            <Star
              className="h-6 w-6"
              style={
                filled
                  ? { color: ink, fill: ink }
                  : { color: 'rgba(96,124,110,0.40)' }
              }
            />
          </button>
        );
      })}
    </div>
  );
}

export function QCReportFormDialog({
  open,
  onOpenChange,
  siteId,
  siteName,
  token,
  language,
  onCreated,
}: QCReportFormDialogProps) {
  const text = copy[language];
  const [visit, setVisit] = useState<QCVisitOption | null>(null);
  const [entries, setEntries] = useState<BlockEntry[]>([]);
  const [coversExtras, setCoversExtras] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Load visit + blocks whenever dialog opens.
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setVisit(null);
    setEntries([]);
    setCoversExtras(false);
    setSubmitError(null);

    (async () => {
      try {
        const v = await fetchLatestVisit(token, siteId);
        if (cancelled) return;
        setVisit(v);
        if (!v) {
          setIsLoading(false);
          return;
        }
        const blocks = await fetchVisitBlocks(token, v.job_id);
        if (cancelled) return;
        setEntries(blocks.map((b) => ({ block: b, score: null, notes: '' })));
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load visit');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, siteId, token]);

  // Reset scroll when content loads.
  useEffect(() => {
    if (!open || isLoading) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [open, isLoading]);

  const ratedEntries = useMemo(
    () => entries.filter((e) => e.score != null),
    [entries],
  );
  const average = useMemo(() => {
    if (ratedEntries.length === 0) return null;
    const sum = ratedEntries.reduce((acc, e) => acc + (e.score ?? 0), 0);
    return sum / ratedEntries.length;
  }, [ratedEntries]);

  const blockingMissingNote = entries.some(
    (e) => e.score === 1 && e.notes.trim().length === 0,
  );
  const canSubmit =
    !isSubmitting
    && !!visit
    && !!token
    && ratedEntries.length > 0
    && !blockingMissingNote;

  const updateEntry = (index: number, patch: Partial<Omit<BlockEntry, 'block'>>) => {
    setEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!token || !visit || ratedEntries.length === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createQCReportsBatch(token, {
        site_id: siteId,
        job_id: visit.job_id,
        covers_extras: coversExtras,
        scores: ratedEntries.map((e) => ({
          job_block_id: e.block.id,
          score: e.score as Score,
          notes: e.notes.trim() ? e.notes.trim() : null,
        })),
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden border-[#073331]/12 bg-white p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="cmd-label text-[#073331] text-[11px]">{text.title}</DialogTitle>
          <DialogDescription className="font-mono text-[10px] text-[#607C6E]">
            {siteName} · {text.description}
          </DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto cmd-scroll px-6 py-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 font-mono text-xs text-[#607C6E]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {text.loading}
            </div>
          ) : loadError ? (
            <div
              className="rounded px-3 py-2 font-mono text-xs"
              style={{ border: '1px solid rgba(194,65,56,0.35)', background: 'rgba(194,65,56,0.06)', color: FAIL_INK }}
            >
              {loadError}
            </div>
          ) : !visit ? (
            <div
              className="rounded px-3 py-6 text-center font-mono text-xs text-[#607C6E]"
              style={{ border: '1px dashed rgba(7,51,49,0.20)' }}
            >
              {text.noVisit}
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className="rounded px-3 py-2"
                style={{ border: '1px solid rgba(7,51,49,0.12)', background: 'rgba(7,51,49,0.03)' }}
              >
                <div className="cmd-label">{text.visitSummary}</div>
                <div className="mt-1 font-mono text-xs text-[#073331]">
                  {visit.scheduled_for} · {visit.cleaner_name ?? (language === 'sv' ? 'Ej tilldelad' : 'Unassigned')}
                </div>
                <div className="font-mono text-[10px] text-[#607C6E]">
                  {entries.length} {text.blocksHeader.toLowerCase()}
                  {visit.extras_count > 0 && ` · ${visit.extras_count} ${text.extras}`}
                </div>
              </div>

              {entries.length === 0 ? (
                <div
                  className="rounded px-3 py-6 text-center font-mono text-xs text-[#607C6E]"
                  style={{ border: '1px dashed rgba(7,51,49,0.20)' }}
                >
                  {text.none}
                </div>
              ) : (
                <div className="space-y-3">
                  {entries.map((entry, index) => {
                    const { block, score, notes } = entry;
                    const needsNotes = score === 1 && notes.trim().length === 0;
                    return (
                      <div
                        key={block.id}
                        className="rounded p-4"
                        style={{ border: '1px solid rgba(7,51,49,0.12)' }}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-0.5">
                            <div className="font-mono text-xs text-[#073331]">
                              {block.source_code ? `${block.source_code} · ` : ''}
                              {block.area ?? ''}
                            </div>
                            <div className="font-mono text-[10px] text-[#607C6E]">{block.label}</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="cmd-label" style={{ color: statusInk(block.status) }}>
                              {block.status}
                            </span>
                            {block.points != null && (
                              <span className="font-mono tabular-nums text-[10px] text-[#607C6E]">
                                {Number(block.points).toFixed(2)} {text.points}
                              </span>
                            )}
                            <span className="font-mono tabular-nums text-[10px] text-[#607C6E]">
                              {block.photo_count}/{block.required_photos} {text.photos}
                            </span>
                            {block.job_kind === 'extra' && (
                              <span className="cmd-label text-[#8A6A47]">{text.extras}</span>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 space-y-2">
                          <StarPicker
                            value={score}
                            onChange={(next) => updateEntry(index, { score: next })}
                            disabled={isSubmitting}
                          />
                          <Textarea
                            value={notes}
                            onChange={(e) => updateEntry(index, { notes: e.target.value })}
                            rows={2}
                            placeholder={text.notesPlaceholder}
                            disabled={isSubmitting}
                            className="font-mono text-xs"
                          />
                          {needsNotes && (
                            <p className="font-mono text-[10px]" style={{ color: FAIL_INK }}>
                              {text.notesRequired}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {visit.extras_count > 0 && (
                <label className="flex items-center gap-2 font-mono text-xs text-[#2E4640]">
                  <Checkbox
                    checked={coversExtras}
                    onCheckedChange={(value) => setCoversExtras(value === true)}
                    disabled={isSubmitting}
                  />
                  <span>{text.extrasCheck}</span>
                </label>
              )}

              {submitError && (
                <div
                  className="rounded px-3 py-2 font-mono text-xs"
                  style={{ border: '1px solid rgba(194,65,56,0.35)', background: 'rgba(194,65,56,0.06)', color: FAIL_INK }}
                >
                  {text.submitErrorPrefix} {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-[#073331]/10 bg-white px-6 py-4">
          <div className="mr-auto flex items-center gap-3">
            <span className="cmd-label text-[#607C6E]">
              <span className="font-mono tabular-nums text-[#073331]">{ratedEntries.length}/{entries.length}</span> {text.rated}
            </span>
            {average != null && (
              <span className="cmd-label text-[#607C6E]">
                {text.average}{' '}
                <span
                  className="font-mono tabular-nums"
                  style={{ color: average >= 3 ? OK_INK : FAIL_INK }}
                >
                  {average.toFixed(2)}
                </span>
              </span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="cmd-label border-[#073331]/12 text-[#8A6A47] hover:bg-[#073331]/[0.04] hover:text-[#073331]"
          >
            {text.cancel}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="cmd-label bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]"
          >
            {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            <span className={isSubmitting ? 'ml-1' : ''}>
              {isSubmitting ? text.submitting : text.submit}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
